import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search, X, Download, AlertTriangle, CheckCircle } from 'lucide-react';
import MainService from '../../services/MainService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { getAppUi } from '../../locales/appUi';
import DataColumnMenu from '../ui/DataColumnMenu';
import { columnHasActiveFilter, groupRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { flattenFiltersForApi } from '../../utils/listFilters';
import { PAGE_SIZE_OPTIONS, buildPageItems } from '../../utils/listPagination';
import { exportGroupedExcel } from '../../utils/excelExport';

const buildColumns = (ui) => [
  { field: 'refno', title: ui('columns.refNo'), widthClass: 'w-[10%]', hideable: true },
  { field: 'date', title: ui('columns.date'), widthClass: 'w-[9%]', hideable: true },
  { field: 'clients', title: ui('columns.clients'), widthClass: 'w-[18%]', hideable: true },
  { field: 'employee', title: ui('columns.employee'), widthClass: 'w-[12%]', hideable: true },
  { field: 'createdby', title: ui('columns.createdBy'), widthClass: 'w-[11%]', hideable: true },
  { field: 'totalorders', title: ui('columns.totalOrders'), widthClass: 'w-[10%]', hideable: true, align: 'right' },
  { field: 'status', title: ui('common.status'), widthClass: 'w-[12%]', hideable: true },
  { field: 'actions', title: ui('columns.options'), widthClass: 'w-[12%]', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const defaultVisibility = (columns) =>
  columns.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
};

const formatClients = (item) => (item.ClientsNames?.length ? item.ClientsNames.join(', ') : '-');

const getStatusColor = (status) => {
  switch (status) {
    case 'NEW':
      return 'bg-blue-100 text-blue-800';
    case 'APPROVED':
      return 'bg-green-100 text-green-800';
    case 'STATUS DONE':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

const getCellValue = (item, field) => {
  switch (field) {
    case 'refno':
      return item.RefNo || '-';
    case 'date':
      return formatDate(item.Date);
    case 'clients':
      return formatClients(item);
    case 'employee':
      return item.Employee?.Name || '-';
    case 'createdby':
      return item.Createdby?.UserName || '-';
    case 'totalorders':
      return item.TotalOrders || 0;
    case 'status':
      return item.Status || '-';
    default:
      return item[field];
  }
};

const PendingReturns = () => {
  const { language } = useLanguage();
  const ui = useMemo(() => getAppUi(language), [language]);
  const COLUMNS = useMemo(() => buildColumns(ui), [ui]);
  const navigate = useNavigate();

  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [voucherNo, setVoucherNo] = useState('');
  const [activeKeyword, setActiveKeyword] = useState('');
  const [activeVoucherNo, setActiveVoucherNo] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() => defaultVisibility(buildColumns(getAppUi('en'))));
  const [groupField, setGroupField] = useState(null);
  const loadSeqRef = useRef(0);
  const pageSizeBeforeGroupRef = useRef(null);

  useEffect(() => {
    setColumnVisibility((prev) => {
      const next = defaultVisibility(COLUMNS);
      COLUMNS.forEach((col) => {
        if (prev[col.field] !== undefined) next[col.field] = prev[col.field];
      });
      return next;
    });
  }, [COLUMNS]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchReturns = useCallback(async ({
    page: pageArg,
    pageSize: pageSizeArg,
    keyword: keywordArg,
    voucher: voucherArg,
    sortField: sortFieldArg,
    sortDirection: sortDirectionArg,
    filters: filtersArg,
  } = {}) => {
    const pageNum = pageArg ?? page;
    const size = pageSizeArg !== undefined ? pageSizeArg : pageSize;
    const keyword = keywordArg !== undefined ? keywordArg : activeKeyword;
    const voucher = voucherArg !== undefined ? voucherArg : activeVoucherNo;
    const sField = sortFieldArg !== undefined ? sortFieldArg : sortField;
    const sDir = sortDirectionArg !== undefined ? sortDirectionArg : sortDirection;
    const filters = filtersArg !== undefined ? filtersArg : filtersByField;
    const seq = ++loadSeqRef.current;
    const isAll = size === 'all';

    try {
      setLoading(true);
      setError('');
      const body = {
        Skip: isAll ? 0 : (pageNum - 1) * Number(size),
        Take: isAll ? 0 : Number(size),
        Type: 'StatusPage',
        Keyword: (keyword || '').trim() || null,
        VoucherNo: (voucher || '').trim() || null,
        SortField: sField || null,
        SortDir: sDir || 'asc',
        Filters: flattenFiltersForApi(filters),
      };

      const response = await MainService.ClientReturnsListList(body);
      if (seq !== loadSeqRef.current) return;
      setReturns(Array.isArray(response?.Data) ? response.Data : []);
      setTotalCount(Number(response?.Total) || 0);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Error loading pending returns:', err);
      setError('Failed to load pending returns');
      setReturns([]);
      setTotalCount(0);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [page, pageSize, activeKeyword, activeVoucherNo, sortField, sortDirection, filtersByField]);

  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);

  const effectivePageSize = pageSize === 'all' ? Math.max(totalCount, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalCount / effectivePageSize) || 1);
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const rangeFrom = totalCount === 0 ? 0 : (pageSize === 'all' ? 1 : (page - 1) * Number(pageSize) + 1);
  const rangeTo = totalCount === 0 ? 0 : (pageSize === 'all' ? totalCount : Math.min(page * Number(pageSize), totalCount));
  const colSpan = useMemo(
    () => COLUMNS.filter((c) => columnVisibility[c.field] !== false).length + 1,
    [COLUMNS, columnVisibility]
  );
  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const displayGroups = useMemo(() => {
    if (!groupField) return [{ key: null, label: null, rows: returns }];
    const mapped = returns.map((r) => ({ ...r, [groupField]: getCellValue(r, groupField) }));
    return groupRows(mapped, groupField);
  }, [returns, groupField]);

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);
  const groupKeys = useMemo(
    () => displayGroups.filter((g) => g.label != null).map((g) => g.key),
    [displayGroups]
  );

  const handleSearch = () => {
    const k = searchKeyword.trim();
    const v = voucherNo.trim();
    setPage(1);
    setActiveKeyword(k);
    setActiveVoucherNo(v);
    fetchReturns({ page: 1, keyword: k, voucher: v });
  };

  const handleRefresh = () => {
    setPage(1);
    setSearchKeyword('');
    setVoucherNo('');
    setActiveKeyword('');
    setActiveVoucherNo('');
    setSortField('');
    setSortDirection('asc');
    setFiltersByField({});
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    fetchReturns({
      page: 1,
      keyword: '',
      voucher: '',
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const handleGroupBy = (field) => {
    if (!groupField) {
      pageSizeBeforeGroupRef.current = { pageSize, page };
    }
    setGroupField(field);
    if (pageSize !== 'all') {
      setPage(1);
      setPageSize('all');
      fetchReturns({ page: 1, pageSize: 'all' });
    }
  };

  const handleUngroup = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    if (prev) {
      setPageSize(prev.pageSize);
      setPage(prev.page);
      fetchReturns({ page: prev.page, pageSize: prev.pageSize });
    }
  };

  const handlePageSizeChange = (value) => {
    const next = value === 'all' ? 'all' : Number(value);
    setPageSize(next);
    setPage(1);
    fetchReturns({ page: 1, pageSize: next });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    fetchReturns({ page: nextPage });
  };

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
    fetchReturns({ page: 1, sortField: field, sortDirection: direction });
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = COLUMNS.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, filter) => {
    const nextFilters = { ...filtersByField, [field]: filter };
    setFiltersByField(nextFilters);
    setPage(1);
    fetchReturns({ page: 1, filters: nextFilters });
  };

  const handleClearColumnFilter = (field) => {
    const nextFilters = { ...filtersByField };
    delete nextFilters[field];
    setFiltersByField(nextFilters);
    setPage(1);
    fetchReturns({ page: 1, filters: nextFilters });
  };

  const clearSortAndFilters = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setFiltersByField({});
    setSortField('');
    setSortDirection('asc');
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    setPage(1);
    if (prev && prev.pageSize !== pageSize) {
      setPageSize(prev.pageSize);
    }
    fetchReturns({
      page: 1,
      pageSize: prev?.pageSize ?? pageSize,
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const exportToExcel = () => {
    try {
      exportGroupedExcel({
        sheetName: 'Pending Returns',
        fileName: `PendingReturns_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['#', ...COLUMNS.filter((c) => c.field !== 'actions').map((c) => c.title)],
        groups: displayGroups,
        groupFieldTitle: COLUMNS.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (item, serial) => [
          serial,
          ...COLUMNS.filter((c) => c.field !== 'actions').map((c) => getCellValue(item, c.field)),
        ],
      });
      setSuccess('Excel file exported successfully');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      if (err?.message === 'No rows to export') {
        setError('No data to export');
      } else {
        setError('Failed to export Excel file');
      }
    }
  };

  const renderCell = (item, column) => {
    if (column.field === 'clients') {
      const text = formatClients(item);
      return <span className="block max-w-xs truncate" title={text === '-' ? '' : text}>{text}</span>;
    }
    if (column.field === 'status') {
      return (
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(item.Status)}`}>
          {item.Status || '-'}
        </span>
      );
    }
    if (column.field === 'actions') {
      return (
        <button
          type="button"
          onClick={() => navigate(`/home/returns/return-status/${item.Id}`)}
          className="px-3 py-1.5 text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors text-sm font-medium"
          title="Update Status"
        >
          Update Status
        </button>
      );
    }
    return getCellValue(item, column.field);
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError('')} className="flex-shrink-0 text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700 shadow-lg">
          <CheckCircle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button type="button" onClick={() => setSuccess('')} className="flex-shrink-0 text-green-500 hover:text-green-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'pendingReturns')}</h1>
            <p className="text-sm text-gray-500 mt-1">View and manage pending client returns</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportToExcel}
              disabled={loading || returns.length === 0}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Export to Excel"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Keyword</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by Ref No, Client Name, Mobile..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          <div className="col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Voucher No</label>
            <input
              type="text"
              value={voucherNo}
              onChange={(e) => setVoucherNo(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by Voucher No..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="col-span-2 flex items-end">
            <button
              type="button"
              onClick={handleSearch}
              disabled={loading}
              className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Search className="w-4 h-4" />
              Search
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="master-table-scroll relative overflow-x-auto" style={{ maxHeight: 560 }}>
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-gray-600">Loading returns...</span>
              </div>
            </div>
          )}

          {(activeFilterCount > 0 || sortField) && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
              <span>
                {sortField
                  ? `Sorted by ${COLUMNS.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                  : 'Filters active'}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
              </span>
              <button type="button" onClick={clearSortAndFilters} className="text-primary hover:underline">
                Clear sort & filters
              </button>
            </div>
          )}

          {groupField && (
            <GroupByBanner
              groupTitle={COLUMNS.find((c) => c.field === groupField)?.title || groupField}
              groupKeys={groupKeys}
              onUngroup={handleUngroup}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          )}

          <table className="w-full min-w-[1100px] table-fixed">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="w-[4%] px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {ui('columns.sNo')}
                </th>
                {COLUMNS.map((column) =>
                  columnVisibility[column.field] === false ? null : (
                    <DataColumnMenu
                      key={column.field}
                      column={column}
                      columns={COLUMNS}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                      columnVisibility={columnVisibility}
                      onToggleColumn={handleToggleColumn}
                      filtersByField={filtersByField}
                      onApplyFilter={handleApplyColumnFilter}
                      onClearFilter={handleClearColumnFilter}
                      groupField={groupField}
                      onGroupBy={handleGroupBy}
                      onUngroup={handleUngroup}
                      align={column.align}
                    />
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {returns.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-6 py-12 text-center text-gray-500">
                    <p className="text-lg mb-2">No returns found</p>
                  </td>
                </tr>
              ) : (
                (() => {
                  let serial = 0;
                  return displayGroups.flatMap((group) => {
                    const block = [];
                    if (group.label != null) {
                      block.push(
                        <tr key={`group-${group.key}`} className="bg-gray-100">
                          <td colSpan={colSpan} className="px-4 py-2">
                            <GroupHeaderLabel
                              title={COLUMNS.find((c) => c.field === groupField)?.title}
                              label={group.label}
                              count={group.rows.length}
                              collapsed={isCollapsed(group.key)}
                              onToggle={() => toggleGroup(group.key)}
                            />
                          </td>
                        </tr>
                      );
                    }
                    if (group.label != null && isCollapsed(group.key)) {
                      return block;
                    }
                    group.rows.forEach((item, index) => {
                      serial += 1;
                      block.push(
                        <tr key={item.Id || `${group.key}-${index}`} className="hover:bg-gray-50 transition-colors">
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-600">
                            {groupField || pageSize === 'all' ? serial : (page - 1) * Number(pageSize) + serial}
                          </td>
                          {COLUMNS.map((column) =>
                            columnVisibility[column.field] === false ? null : (
                              <td
                                key={column.field}
                                className={`whitespace-nowrap px-4 py-2 text-sm text-gray-600 ${
                                  column.field === 'refno' ? 'font-medium text-gray-900' : ''
                                } ${column.align === 'center' ? 'text-center' : ''} ${column.align === 'right' ? 'text-right' : ''}`}
                              >
                                {renderCell(item, column)}
                              </td>
                            )
                          )}
                        </tr>
                      );
                    });
                    return block;
                  });
                })()
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {totalCount === 0
                  ? 'No returns found'
                  : `Showing ${rangeFrom} to ${rangeTo} of ${totalCount} returns`}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
                {groupField ? ` · grouped by ${COLUMNS.find((c) => c.field === groupField)?.title || groupField}` : ''}
              </span>
              <label className="flex items-center gap-2">
                <span className="text-gray-500">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  disabled={loading || !!groupField}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={String(opt)} value={opt}>
                      {opt === 'all' ? 'All' : opt}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!groupField && pageSize !== 'all' && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                {pageItems.map((item, idx) =>
                  item === '…' ? (
                    <span key={`e-${idx}`} className="px-1.5 text-gray-400">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      disabled={loading}
                      onClick={() => goToPage(item)}
                      className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm transition-colors ${
                        item === page
                          ? 'bg-[#46955A] font-medium text-white'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingReturns;
